import { ref, computed } from 'vue'
import { refDebounced } from '@vueuse/core'

/**
 * Composable-функция, инкапсулирующая всю не-визуальную логику
 * для компонента AoGreatSelect.
 * @param {object} props - Реактивные пропсы компонента.
 * @param {function} emit - Функция для отправки событий.
 * @returns {object} - Публичный API с состоянием и методами.
 */
export function useGreatSelect(props, emit) {
	// --- 1. Антикоррупционный слой (Адаптация API) ---
	// Мы немедленно преобразуем "неудобные" пропсы items и itemsFull
	// в переменные с правильными семантическими названиями.
	// Вся остальная логика будет работать только с ними.

	/**
	 * @description Основной, полный список элементов. Источник правды.
	 */
	const primaryList = computed(() => {
		// Если `itemsFull` предоставлен и не пуст, он главный. Иначе главный - `items`.
		if (props.suggestedItems?.length) {
			return props.items
		}
		return props.itemsFull?.length ? props.itemsFull : props.items
	})

	/**
	 * @description Второстепенный, сокращенный список (рекомендации).
	 * Существует только если предоставлен `itemsFull`.
	 */
	const abbreviatedList = computed(() => {
		if (props.suggestedItems?.length) {
			return props.suggestedItems
		}
		return props.itemsFull?.length ? props.items : []
	})

	// --- 2. Индексация для производительности ---

	/**
	 * @description Создаем индексированную карту для мгновенного поиска объектов по их значению.
	 * Эта операция O(N) выполнится только один раз при изменении `primaryList`.
	 * Все последующие доступы будут O(1).
	 */
	const valueToItemMap = computed(() => {
		return new Map(primaryList.value.map((item) => [item[props.itemValue], item]))
	})

	// --- 3. Внутреннее состояние (State) ---

	/**
	 * @description Флаг, показывающий, запросил ли пользователь отображение полного списка.
	 */
	const showFullList = ref(false)

	const searchQuery = ref('')
	const debouncedSearchQuery = refDebounced(searchQuery, 300)
	function updateSearchQuery(value) {
		searchQuery.value = value ?? ''
	}

	// --- 4. Вычисляемые свойства для UI ---

	/**
	 * @description Определяет, активен ли сейчас поиск.
	 */
	const isSearchActive = computed(() => {
		return props.isSearch && debouncedSearchQuery.value.length >= props.searchMinChars
	})

	/**
	 * @description Определяет исходный список для дальнейшей обработки.
	 */
	const sourceForProcessing = computed(() => {
		// Если поиск активен, всегда используем полный (основной) список.
		if (isSearchActive.value) {
			return primaryList.value
		}
		// Если есть сокращенный список и пользователь не открыл полный, используем его.
		const useAbbreviated = !showFullList.value && abbreviatedList.value.length > 0
		return useAbbreviated ? abbreviatedList.value : primaryList.value
	})

	/**
	 * @description Применяет фильтрацию к исходному списку.
	 */
	const filteredItems = computed(() => {
		if (!isSearchActive.value) {
			// Если поиск не активен, возвращаем исходный список как есть.
			return sourceForProcessing.value
		}
		const query = debouncedSearchQuery.value.toLowerCase()
		return sourceForProcessing.value.filter((item) => {
			const title = String(item[props.itemTitle] || '').toLowerCase()
			const value = String(item[props.itemValue] || '').toLowerCase()
			return title.includes(query) || value.includes(query)
		})
	})

	/**
	 * @description Применяет сортировку. Это финальный список для отображения.
	 */
	const itemsToDisplay = computed(() => {
		if (!props.isSorted) {
			return filteredItems.value
		}
		const itemsToSort = [...filteredItems.value]

		const compareFn =
			props.sortCompareCallback ||
			((a, b) => {
				const titleA = a[props.itemTitle]
				const titleB = b[props.itemTitle]
				return titleA > titleB ? 1 : titleB > titleA ? -1 : 0
			})

		return itemsToSort.sort(compareFn)
	})

	/**
	 * @description Вычисляет объект(ы) выбранных элементов с использованием быстрой карты.
	 */
	const selectedItems = computed(() => {
		const map = valueToItemMap.value

		if (props.multiselect) {
			const selectedValues = props.modelValue || []
			return selectedValues.map((value) => map.get(value)).filter(Boolean) // .filter(Boolean) для удаления undefined, если значение не найдено
		}

		return map.get(props.modelValue) || null
	})

	/**
	 * @description Проверяет, выбраны ли все элементы в ТЕКУЩЕМ отфильтрованном списке.
	 */
	const isAllSelected = computed(() => {
		// Эта функция имеет смысл только для мультиселекта и если есть что выбирать
		if (!props.multiselect || itemsToDisplay.value.length === 0) {
			return false
		}

		const currentValues = new Set(props.modelValue || [])
		// Проверяем, что КАЖДЫЙ элемент из видимого списка присутствует в `modelValue`
		return itemsToDisplay.value.every((item) => currentValues.has(item[props.itemValue]))
	})

	/**
	 * @description Определяет, нужно ли отображать переключатель полного/сокращенного списка.
	 * Он нужен только если есть оба списка и поиск не активен.
	 */
	const isShowFullListToggleAvailable = computed(() => {
		// У переключателя нет смысла, если нет сокращенного списка или активен поиск
		return abbreviatedList.value.length > 0 && !isSearchActive.value
	})

	// --- 5. Методы ---

	/**
	 * @description Обрабатывает выбор элемента из списка.
	 * @param {object} item - Выбранный объект элемента.
	 */
	function handleItemSelect(item) {
		const clickedValue = item[props.itemValue]

		if (props.multiselect) {
			const currentModel = [...(props.modelValue || [])]
			const valueIndex = currentModel.indexOf(clickedValue)

			if (valueIndex > -1) {
				currentModel.splice(valueIndex, 1)
			} else {
				currentModel.push(clickedValue)
			}
			emit('update:modelValue', currentModel)
		} else {
			emit('update:modelValue', clickedValue)
		}
	}

	/**
	 * @description Выбирает все видимые элементы или снимает с них выбор.
	 */
	function toggleSelectAll() {
		const currentValues = new Set(props.modelValue || [])
		const visibleValues = itemsToDisplay.value.map((item) => item[props.itemValue])

		if (isAllSelected.value) {
			// Если все выбраны -> удаляем все видимые
			const newValues = Array.from(currentValues).filter((value) => !visibleValues.includes(value))
			emit('update:modelValue', newValues)
		} else {
			// Если не все выбраны -> добавляем все видимые (избегая дубликатов)
			visibleValues.forEach((value) => currentValues.add(value))
			emit('update:modelValue', Array.from(currentValues))
		}
	}

	const onClickClear = () => {
		emit('update:modelValue', props.multiselect ? [] : null)
	}

	/**
	 * @description Переключает флаг отображения полного списка.
	 */
	function toggleShowFullList() {
		showFullList.value = !showFullList.value
	}

	// --- 6. Возвращаем публичный API ---
	// Отдаем наружу только то, что нужно компоненту-оркестратору.
	return {
		// Данные для UI
		itemsToDisplay,
		selectedItems,

		// Состояние для UI (например, для иконки-переключателя)
		showFullList,

		searchQuery,
		updateSearchQuery,

		// Методы для UI
		handleItemSelect,

		isAllSelected,
		toggleSelectAll,
		onClickClear,

		isShowFullListToggleAvailable,
		toggleShowFullList,
	}
}
